import { Link } from 'react-router-dom';
import { PILOT_MAILTO } from '../data/plans';
import PageWorld from '../components/site/PageWorld';
import { GATE } from '../data/worlds';
import { PageFilm } from '../components/PageFilm';

const GATE_CHAPTERS = [
  { at: 9.33, title: 'What a gate is' },
  { at: 52.67, title: 'How a step picks its model' },
];
const GATE_SEEK = { hash: '#routing', at: 52.67, scrollTo: 'gate-film' };

// /gate — what a gate actually is, and how model routing works. The two
// structural claims the homepage #proofs strip points at. Every sentence
// here describes shipped behaviour (shared/blocks/handlers.js,
// shared/blocks/agentosGraph.js, shared/models/policy.js, liveConfig
// models.policy). No credits-per-commit arithmetic — it is not verified.
// Positioning: [[website-product-first-messaging]] (2026-10-08 amendment).

const COMPARE = [
  ['Who holds the credential', 'The agent process. The approval screen sits in front of a tool the model can already call.', 'The runtime. The model never receives a write-capable tool or a secret; secrets resolve from SSM at commit time.'],
  ['What is approved', 'A tool call the model composed.', 'A payload against a capability you declared on the pipeline. If you did not declare the write, the call does not exist — not a rule the model follows, a door that is not there.'],
  ['Who can approve', 'Often the model itself ("confirm: yes").', 'A person, or a keyword filter. An LLM yes or no is not a gate.'],
  ['What a retry does', 'Posts again.', 'Hits the same idempotency key. The receiver dedupes; the ledger shows one commit.'],
  ['What the record says', 'A log line.', 'Trace id from the conversation to the block, the policy version in force, the reviewer, the runtime identity, the capability.'],
  ['Several agents on one job', 'Each one with its own tools.', 'A coordinator block runs the roles; none of them can commit. Only the pipeline\u2019s gated side effect does.'],
];

const TIERS = [
  ['local', 'An open-weight model on your own node or the appliance. Classification, extraction, drafts that stay inside.'],
  ['bulk', 'A low-cost hosted model. The default for most steps.'],
  ['strong', 'A capable hosted model. Required when a call carries tools or a long context.'],
  ['frontier', 'The largest models. Reached by escalation or an explicit pin, under a share cap.'],
];

export default function Gate() {
  return (
    <PageWorld world={GATE}>
    <div className="cs-store" data-nova-note="The Gate page: what a gate is — the approval step every write and deploy passes — and how Human Review uses it.">
      <header className="cs-store-hero">
        <h1>What a gate actually is</h1>
        <p style={{ maxWidth: 720 }}>
          Most agent products now have an approval button. Clicking yes
          before a model calls a tool is not the same as a model that
          cannot call it. Here is the difference, in the terms your
          security review will use.
        </p>
      </header>

      <PageFilm
        base="/media/gate-film"
        prefix="gate"
        title="What a gate actually is"
        summary="Not allowed means not possible. The model holds no tool that writes; a person or a keyword rule decides; the runtime posts with its own credentials. The same gate sits on a branch, a loop, or a call, and production requires a person."
        chapters={GATE_CHAPTERS}
        hashSeek={GATE_SEEK}
      />

      <section className="cs-store-section" style={{ maxWidth: 900 }}>
        <h2>Approval on a tool vs a structural gate</h2>
        <div className="cs-gate-table" role="table">
          <div className="cs-gate-row cs-gate-head" role="row">
            <span role="columnheader" />
            <span role="columnheader">Approval in front of a tool</span>
            <span role="columnheader">A gate on agentOS</span>
          </div>
          {COMPARE.map(([k, a, b]) => (
            <div key={k} className="cs-gate-row" role="row">
              <strong role="rowheader">{k}</strong>
              <span role="cell">{a}</span>
              <span role="cell">{b}</span>
            </div>
          ))}
        </div>
        <p className="cs-land-micro">
          The shape of it: the model proposes a payload. The pipeline names
          the capability it is for. A review step &mdash; a person, or a
          keyword filter &mdash; decides. The runtime commits with its own
          credentials and writes the receipt. Design the pipeline in{' '}
          <Link to="/platform/nqube">nQube</Link>; the runtime is{' '}
          <Link to="/platform/agentos">agentOS</Link>.
        </p>
      </section>

      <section className="cs-store-section" id="routing" style={{ maxWidth: 900 }}>
        <h2>How a step picks its model</h2>
        <p className="cs-store-note" style={{ maxWidth: 760 }}>
          Reliability lives in the runtime, so the model does not have to
          be the biggest one available. Each call starts on the lowest tier
          its route allows and moves up only on a failure the runtime can
          verify.
        </p>
        <ul className="cs-gate-tiers">
          {TIERS.map(([k, v]) => (
            <li key={k}><strong>{k}</strong><span>{v}</span></li>
          ))}
        </ul>
        <div className="cs-land-price-grid">
          <div className="cs-sw-card">
            <h3>Rules</h3>
            <p>A call that carries tools, or more than 60k prompt tokens, starts at <code>strong</code>. Routes can set a starting tier per stage &mdash; triage on bulk, the planner on strong.</p>
          </div>
          <div className="cs-sw-card">
            <h3>Escalation</h3>
            <p>Bad JSON, a tool-parse error, an empty answer, or a faithfulness score under threshold moves the call up one tier and retries &mdash; at most twice. A vague answer does not count.</p>
          </div>
          <div className="cs-sw-card">
            <h3>Caps</h3>
            <p>A ceiling on the hourly share of tokens that reach <code>frontier</code>; past it, policy-resolved calls use <code>strong</code>. A monthly spend cap per organization that stops with a 402.</p>
          </div>
          <div className="cs-sw-card">
            <h3>Your endpoint</h3>
            <p>Point a tier at an https endpoint you control, with your key. A model you trained is a name you serve there. A box with no inbound door is not reached by a tunnel &mdash; that is not built. It needs an https front door, or it runs on the App Kit network below.</p>
          </div>
          <div className="cs-sw-card">
            <h3>On the instance</h3>
            <p>The App Kit self-host stamp runs vLLM or Ollama on the instance network. Nothing is published. The <code>local</code> tier is that model.</p>
          </div>
          <div className="cs-sw-card">
            <h3>Dev Console gateway</h3>
            <p>Dev tools, hosted instances and the Dev Console call one OpenAI-compatible endpoint and name a virtual model &mdash; <code>nventr/auto</code>, <code>strong</code>, <code>bulk</code>, <code>local</code>, <code>nventr/embed</code>, <code>nventr/image</code>, <code>nventr/video</code> &mdash; and the policy above decides. Or name any model in the catalog directly: chat, embeddings, image and video generation across OpenAI, Anthropic, xAI, Google, OpenRouter and fal.ai. Each plan includes a monthly hosted-model allowance; past it, calls draw a prepaid balance you top up, and keys you bring are never metered. We do not train the model.</p>
          </div>
        </div>
        <p className="cs-land-micro">
          All of it is live configuration on your organization: tiers,
          starting routes, escalation triggers, caps. Change a number, not a
          deploy.
        </p>
        <div className="cs-land-cta-row">
          <a href={PILOT_MAILTO} className="cs-btn cs-btn-primary">Run this on your documents</a>
          <Link to="/pricing" className="cs-btn cs-btn-outline">Pricing</Link>
        </div>
      </section>
    </div>
    </PageWorld>
  );
}
